import { RouteReuseStrategy, ActivatedRouteSnapshot, DetachedRouteHandle } from '@angular/router';

export class CustomRouteReuseStrategy implements RouteReuseStrategy {
  // Store of detached routes, keyed by route path
  private storedRoutes = new Map<string, DetachedRouteHandle>();

  // Determine if this route should be reused (i.e., attach a previously detached component)
  // This is called when navigating TO a route
  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    // Only attach if the route has a key and we have a stored handle for it
    const routeKey = this.getRouteKey(route);
    const shouldAttach = !!routeKey && this.storedRoutes.has(routeKey);
    // console.log(`shouldAttach for ${routeKey}: ${shouldAttach}`);
    return shouldAttach;
  }

  // Retrieve the previously stored route handle
  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    if (!route.routeConfig || route.routeConfig.loadChildren) {
      // Don't store lazy loaded routes or routes without config
      return null;
    }
    const routeKey = this.getRouteKey(route);
    const retrieved = this.storedRoutes.get(routeKey);
    // console.log(`retrieve for ${routeKey}: ${retrieved ? 'found' : 'not found'}`);
    return retrieved || null;
  }

  // Determine if the route should be detached (i.e., keep its instance in memory)
  // This is called when navigating AWAY FROM a route
  shouldDetach(route: ActivatedRouteSnapshot): any {
    // Only detach specific routes that you want to cache.
    // In your case, you likely want to cache the 'agro-dealer-list' route.
    const shouldDetach = route.routeConfig && route.routeConfig.path === 'dealers';
    // console.log(`shouldDetach for ${route.routeConfig?.path}: ${shouldDetach}`);
    return shouldDetach;
  }

  // Store the detached route handle
  store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle | null): void {
    const routeKey = this.getRouteKey(route);
    if (handle && routeKey) {
      this.storedRoutes.set(routeKey, handle);
      // console.log(`stored ${routeKey}`);
    } else if (routeKey && !handle) {
      this.storedRoutes.delete(routeKey); // Clear if no handle (e.g., component was destroyed)
      // console.log(`cleared ${routeKey}`);
    }
  }

  // Determine if a route should be reused (i.e., keep the component instance when navigating to the same route)
  // This is called when navigating between instances of the same route (e.g., /details/1 to /details/2)
  shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    // For exact match, reuse the route if their paths and parameters are the same
    // Or, you can customize this based on your needs.
    // For simply preventing re-initialization on back navigation, the default behavior here is usually fine.
    const reuse = future.routeConfig === curr.routeConfig && this.getRouteKey(future) === this.getRouteKey(curr);
    // console.log(`shouldReuseRoute for ${this.getRouteKey(future)} vs ${this.getRouteKey(curr)}: ${reuse}`);
    return reuse;
  }

  // Helper to generate a unique key for the route, including its parameters
  private getRouteKey(route: ActivatedRouteSnapshot): string {
    if (!route.routeConfig || !route.routeConfig.path) {
      return '';
    }
    // You might need to include query params if they significantly change the state.
    // For now, just using the path should be sufficient for your scenario.
    // If your /agro-dealer-list can have dynamic parts or query parameters that define different states,
    // you'd need to include them in the key.
    // e.g., `${route.routeConfig.path}_${JSON.stringify(route.params)}_${JSON.stringify(route.queryParams)}`;
    return route.routeConfig.path;
  }
}